/**
 * Analytics.ts - GA4 Analytics Integration
 * Implements event tracking with graceful CSP failure handling
 * Uses send_to grouping to avoid polluting host website analytics
 */

export interface AnalyticsConfig {
  measurementId: string;
  debug?: boolean;
  enabled?: boolean;
}

export type AtmosphereEvent = 
  | 'snow_start'
  | 'snow_stop'
  | 'egg_triggered'
  | 'snow_mute_clicked'
  | 'config_changed'
  | 'benchmark_complete';

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

// GA4 gtag interface
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export class Analytics {
  private measurementId: string;
  private debug: boolean;
  private enabled: boolean;
  private initialized: boolean = false;
  private initializationFailed: boolean = false;

  constructor(config: AnalyticsConfig) {
    this.measurementId = config.measurementId;
    this.debug = config.debug ?? false;
    this.enabled = config.enabled ?? true;
  }

  /**
   * Initialize GA4 by injecting the gtag script
   * Fails gracefully if CSP blocks the script
   */
  async initialize(): Promise<boolean> {
    if (this.initialized) return true;
    if (this.initializationFailed) return false;
    if (!this.enabled) {
      this.log('Analytics disabled');
      return false;
    }

    // Check if gtag already exists (host may have GA)
    if (typeof window.gtag === 'function') {
      this.initialized = true;
      this.configureGtag();
      this.log('Using existing gtag instance');
      return true;
    }

    try {
      await this.injectGtagScript();
      this.configureGtag();
      this.initialized = true;
      this.log('GA4 initialized successfully');
      return true;
    } catch (error) {
      this.initializationFailed = true;
      this.log('GA4 initialization failed (likely CSP block):', error);
      // Don't throw - just return false and continue without analytics
      return false;
    }
  }

  /**
   * Inject the gtag.js script dynamically
   */
  private injectGtagScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create script element
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;

      // Set up load/error handlers
      const timeout = setTimeout(() => {
        reject(new Error('GA4 script load timeout'));
      }, 5000);

      script.onload = () => {
        clearTimeout(timeout);
        
        // Initialize dataLayer and gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(...args: unknown[]) {
          window.dataLayer!.push(args);
        };
        window.gtag('js', new Date());
        
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('GA4 script failed to load'));
      };

      // Append to head
      try {
        document.head.appendChild(script);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Configure gtag with our measurement ID and settings
   * Uses send_to to isolate our events from host website
   */
  private configureGtag(): void {
    if (!window.gtag) return;

    window.gtag('config', this.measurementId, {
      // Disable automatic page_view to avoid conflicts
      send_page_view: false,
      // Enable debug mode if requested
      debug_mode: this.debug,
      // Custom dimension for identification
      custom_map: {
        dimension1: 'atmosphere_version',
      },
    });
  }

  /**
   * Track an Atmosphere event
   * @param event - The event name
   * @param params - Optional event parameters
   */
  track(event: AtmosphereEvent, params?: EventParams): void {
    if (!this.enabled) return;
    
    // If not initialized, try to initialize first
    if (!this.initialized && !this.initializationFailed) {
      this.initialize().then(() => {
        this.sendEvent(event, params);
      });
      return;
    }

    this.sendEvent(event, params);
  }

  /**
   * Internal method to send events
   */
  private sendEvent(event: AtmosphereEvent, params?: EventParams): void {
    if (!window.gtag || this.initializationFailed) {
      this.log(`Event queued (no gtag): ${event}`, params);
      return;
    }

    const eventParams = {
      ...params,
      // Use send_to to ensure events go to our property only
      send_to: this.measurementId,
      // Add namespace prefix to avoid collisions
      event_category: 'atmosphere',
      atmosphere_version: '1.0.0',
    };

    window.gtag('event', event, eventParams);
    this.log(`Event sent: ${event}`, eventParams);
  }

  /**
   * Track snow effect start
   */
  trackSnowStart(config?: { intensity?: number; particleCount?: number }): void {
    this.track('snow_start', {
      intensity: config?.intensity,
      particle_count: config?.particleCount,
    });
  }

  /**
   * Track snow effect stop
   */
  trackSnowStop(): void {
    this.track('snow_stop');
  }

  /**
   * Track easter egg trigger
   */
  trackEggTriggered(eggName: string): void {
    this.track('egg_triggered', {
      egg_name: eggName,
    });
  }

  /**
   * Track mute button click
   */
  trackMuteClicked(duration: 'day' | '3days' | 'forever'): void {
    this.track('snow_mute_clicked', {
      mute_duration: duration,
    });
  }

  /**
   * Track benchmark completion
   */
  trackBenchmarkComplete(level: string, score: number): void {
    this.track('benchmark_complete', {
      performance_level: level,
      benchmark_score: score,
    });
  }

  /**
   * Track configuration change
   */
  trackConfigChanged(setting: string, value: string | number | boolean): void {
    this.track('config_changed', {
      setting_name: setting,
      setting_value: String(value),
    });
  }

  /**
   * Debug logging
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[Atmosphere Analytics] ${message}`, ...args);
    }
  }

  /**
   * Enable or disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if analytics is working
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Check if initialization failed
   */
  hasFailed(): boolean {
    return this.initializationFailed;
  }
}

// Default analytics instance (disabled by default - users must opt-in)
export const analytics = new Analytics({
  measurementId: 'G-XXXXXXXXXX', // Placeholder - should be configured
  enabled: false,
  debug: false,
});

